// src/services/axios/AiCRMApis/AiCRMApis.js
import apiConfig from "../ApiConfig";

/**
 * AiCRMApis - single file for all AiCRM API calls (Option A)
 * Uses apiConfig (axios instance) for requests.
 *
 * Exported functions:
 * - getDashboardStats
 * - getTopCustomers
 * - getCategories
 * - getSubCategories
 * - getCustomerRequirements
 * - updateCustomerRequirement
 * - fetchEnabledModulesForSidebar (Default + Additional)
 * - getCustomers
 * - createCustomer
 * - updateCustomer
 * - updateCustomerStatus
 * - getModules
 * - getAuditLog
 * - fetchEnabledModules
 * - fetchAdditionalModules
 * - updateAdditionalModuleToggle
 */

const AiCRMApis = {
  // Dashboard
  getDashboardStats: async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await apiConfig.get("/api/ai-crm/api/v1/dashboard/stats/", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getDashboardStats error:", err);
      throw err;
    }
  },

  // Top performing customers
  getTopCustomers: async () => {
    try {
      const res = await apiConfig.get("/api/top-performing-customers/");
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getTopCustomers error:", err);
      throw err;
    }
  },

  // Categories & Sub-categories (ai-crm)
  getCategories: async () => {
    try {
      const res = await apiConfig.get("/api/ai-crm/api/v1/categories/");
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getCategories error:", err);
      throw err;
    }
  },

  getSubCategories: async (categoryId) => {
    try {
      const res = await apiConfig.get(
        `/api/ai-crm/api/v1/categories/${categoryId}/sub_categories/`
      );
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getSubCategories error:", err);
      throw err;
    }
  },

  // Customer requirements (Default, TrustCenter, AdditionalModule)
  getCustomerRequirements: async (customerId, type = "Default") => {
    try {
      const res = await apiConfig.get(
        `/api/ai-crm/api/v1/customer-requirements/${customerId}/?type=${type}`
      );
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getCustomerRequirements error:", err);
      throw err;
    }
  },

  // Update a single customer requirement (generic)
  updateCustomerRequirement: async ({ customer_id, type, field, value }) => {
    try {
      const payload = {
        customer_id,
        type,
        field,
        value,
      };
      const res = await apiConfig.put(
        `/api/ai-crm/api/v1/customer-requirements/update_requirement/`,
        payload
      );
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.updateCustomerRequirement error:", err);
      throw err;
    }
  },

  // Combined enabled modules (Default + AdditionalModule) — returns array of module keys
  fetchEnabledModulesForSidebar: async (customerId) => {
    try {
      if (!customerId) return [];
      const defaultRes = await apiConfig.get(
        `/api/ai-crm/api/v1/customer-requirements/${customerId}/?type=Default`
      );
      const addRes = await apiConfig.get(
        `/api/ai-crm/api/v1/customer-requirements/${customerId}/?type=AdditionalModule`
      );

      const defaultModules = Object.keys(defaultRes.data?.data || {}).filter(
        (m) => defaultRes.data.data[m] === true
      );

      const additionalModules = Object.keys(addRes.data?.data || {}).filter(
        (m) => addRes.data.data[m] === true
      );

      const combined = [...new Set([...defaultModules, ...additionalModules])];
      return combined;
    } catch (err) {
      console.error(
        "AiCRMApis.fetchEnabledModulesForSidebar error:",
        err
      );
      throw err;
    }
  },

  // Customers (listing, pagination)
  getCustomers: async ({ page = 1, page_size = 25, search_term = "", filters = {} }) => {
    try {
      const params = {
        page,
        page_size,
        search_term,
        ...filters,
      };

      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") {
          query.append(k, v);
        }
      });

      const res = await apiConfig.get(`/api/ai-crm/api/v1/leads/?${query.toString()}`);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getCustomers error:", err);
      throw err;
    }
  },

  // Create / Update customer
  createCustomer: async (data) => {
    try {
      const res = await apiConfig.post(`/api/ai-crm/api/v1/leads/`, data);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.createCustomer error:", err);
      throw err;
    }
  },

  updateCustomer: async (customerId, data) => {
    try {
      const res = await apiConfig.put(`/api/ai-crm/api/v1/leads/${customerId}/`, data);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.updateCustomer error:", err);
      throw err;
    }
  },

  // Update customer status (suspend/activate/etc)
  updateCustomerStatus: async (customerId, payload) => {
    try {
      const res = await apiConfig.put(`/api/ai-crm/api/v1/leads/${customerId}/status/`, payload);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.updateCustomerStatus error:", err);
      throw err;
    }
  },

  // Modules (available modules)
  getModules: async () => {
    try {
      const res = await apiConfig.get(`/api/modules`);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getModules error:", err);
      throw err;
    }
  },

  // Audit log
  getAuditLog: async (customerId) => {
    try {
      const res = await apiConfig.get(`/api/customers/${customerId}/audit-log`);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getAuditLog error:", err);
      throw err;
    }
  },

  // Fetch enabled modules for a given user/company (other endpoints used in UI)
  fetchEnabledModules: async (customerId) => {
    try {
      const res = await apiConfig.get(`/api/customer-enabled-modules/${customerId}/`);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.fetchEnabledModules error:", err);
      throw err;
    }
  },

  // Additional modules fetch & toggle helpers (for UI flows)
  fetchAdditionalModules: async (customerId) => {
    try {
      const res = await apiConfig.get(
        `/api/ai-crm/api/v1/customer-requirements/${customerId}/?type=AdditionalModule`
      );
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.fetchAdditionalModules error:", err);
      throw err;
    }
  },

  updateAdditionalModuleToggle: async ({ customer_id, moduleName, value }) => {
    try {
      const res = await apiConfig.put(`/api/ai-crm/api/v1/customer-requirements/update_requirement/`, {
        customer_id,
        type: "AdditionalModule",
        field: moduleName,
        value,
      });
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.updateAdditionalModuleToggle error:", err);
      throw err;
    }
  },

  // Billing data
  getBillingData: async () => {
    try {
      const res = await apiConfig.get("/api/ai-crm/api/v1/billing/");
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getBillingData error:", err);
      throw err;
    }
  },

  // Follow-up operations
  addFollowUp: async (customerId, data) => {
    try {
      const res = await apiConfig.post(`/api/ai-crm/api/v1/leads/${customerId}/add_follow_up/`, data);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.addFollowUp error:", err);
      throw err;
    }
  },

  updateFollowUp: async (customerId, data) => {
    try {
      const res = await apiConfig.patch(`/api/ai-crm/api/v1/leads/${customerId}/update_follow_up/`, data);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.updateFollowUp error:", err);
      throw err;
    }
  },

  // Update lead/customer with PATCH
  patchCustomer: async (customerId, data) => {
    try {
      const res = await apiConfig.patch(`/api/ai-crm/api/v1/leads/${customerId}/`, data);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.patchCustomer error:", err);
      throw err;
    }
  },

  // Get CRM audit logs
  getCRMLogs: async (limit = 500, offset = 0) => {
    try {
      const res = await apiConfig.get(`/api/ai-crm/crm-logs/?limit=${limit}&offset=${offset}`);
      return res.data;
    } catch (err) {
      console.error("AiCRMApis.getCRMLogs error:", err);
      throw err;
    }
  },
};

export default AiCRMApis;
